import { toast } from '@/lib/toast';
import { convertHeicBatch } from './heicUtils';
import { convertDngIfNeeded } from './dngUtils';

/**
 * Universal photo-input pipeline used by every <input type="file"> in the app.
 *
 * Responsibilities:
 *  - Filter to image MIME types (also accepts .heic/.heif/.dng by extension)
 *  - Convert HEIC/HEIF → JPEG (lazy)
 *  - Convert iPhone ProRAW .dng → JPEG by extracting the embedded preview
 *  - Reject other RAW formats (CR2/NEF/ARW/...) browsers can't render
 *  - Return a clean File[]
 */
export async function processSelectedFiles(
  fileList: FileList | File[] | null | undefined,
  opts?: { maxCount?: number; multiple?: boolean; onProgress?: (done: number, total: number, phase: 'dng' | 'heic') => void }
): Promise<File[]> {
  if (!fileList) return [];
  const arr = Array.from(fileList as ArrayLike<File>);
  if (arr.length === 0) return [];

  // DNG = iPhone ProRAW (and Adobe). We can salvage these by extracting the
  // embedded full-resolution JPEG preview.
  const DNG_RE = /\.dng$/i;
  const DNG_TYPE_RE = /^image\/x-adobe-dng$/i;
  // Other RAWs we can't read in-browser without a heavy decoder.
  const OTHER_RAW_RE = /\.(cr2|cr3|nef|arw|raf|orf|rw2|srw|pef)$/i;
  const OTHER_RAW_TYPE_RE = /^image\/x-(canon-|nikon-|sony-|fuji-|olympus-|panasonic-)/i;

  const unsupportedRaws = arr.filter(
    (f) => OTHER_RAW_RE.test(f.name) || OTHER_RAW_TYPE_RE.test(f.type)
  );
  if (unsupportedRaws.length > 0) {
    toast.error(
      unsupportedRaws.length === arr.length
        ? "Camera RAW photos can't be uploaded — export as JPEG first."
        : `Skipped ${unsupportedRaws.length} RAW photo(s) — export as JPEG first.`
    );
  }

  // Filter to images. HEIC often has empty type from iOS, DNG also slips
  // past image/* on some browsers — accept both by extension.
  const images = arr.filter((f) => {
    if (OTHER_RAW_RE.test(f.name) || OTHER_RAW_TYPE_RE.test(f.type)) return false;
    if (f.type.startsWith('image/')) return true;
    return /\.(heic|heif)$/i.test(f.name) || DNG_RE.test(f.name);
  });
  if (images.length === 0) {
    if (unsupportedRaws.length === 0) toast.error('Please select image files');
    return [];
  }
  if (images.length + unsupportedRaws.length < arr.length) {
    toast.warning(
      `Skipped ${arr.length - images.length - unsupportedRaws.length} non-image file(s)`
    );
  }

  // Single-file mode: trim to one
  let trimmed = opts?.multiple === false ? images.slice(0, 1) : images;

  if (opts?.maxCount && trimmed.length > opts.maxCount) {
    toast.warning(`Only the first ${opts.maxCount} photo(s) will be added`);
    trimmed = trimmed.slice(0, opts.maxCount);
  }

  // Convert ProRAW .dng → JPEG (extract embedded preview).
  const dngCount = trimmed.filter(
    (f) => DNG_RE.test(f.name) || DNG_TYPE_RE.test(f.type)
  ).length;
  let dngLoadingId: string | number | undefined;
  if (dngCount > 0) {
    dngLoadingId = toast.loading(
      `Converting ${dngCount} ProRAW photo${dngCount > 1 ? 's' : ''}…`
    );
  }
  const dngConverted: File[] = [];
  let dngDone = 0;
  opts?.onProgress?.(0, trimmed.length, 'dng');
  for (const f of trimmed) {
    try {
      dngConverted.push(await convertDngIfNeeded(f));
    } catch (err) {
      console.error('DNG conversion failed:', err);
      toast.error(
        err instanceof Error
          ? err.message
          : `Couldn't read ${f.name}. Turn off ProRAW on iPhone and retry.`
      );
      // skip this file
    }
    dngDone += 1;
    opts?.onProgress?.(dngDone, trimmed.length, 'dng');
  }
  if (dngLoadingId !== undefined) toast.dismiss(dngLoadingId);

  try {
    return await convertHeicBatch(dngConverted, (done, total) =>
      opts?.onProgress?.(done, total, 'heic')
    );
  } catch (err) {
    console.error('HEIC conversion failed:', err);
    toast.error(err instanceof Error ? err.message : 'Could not read one of those photos');
    return dngConverted.filter(
      (f) => !/image\/hei[cf]/i.test(f.type) && !/\.(heic|heif)$/i.test(f.name)
    );
  }
}

/**
 * Reset a file input so re-picking the same file fires onChange again.
 * Always call this after handling a selection.
 */
export function resetFileInput(input: HTMLInputElement | null | undefined) {
  if (input) input.value = '';
}

/**
 * Universal accept string. Kept as bare `image/*` on purpose:
 * iOS Safari's native Photos picker breaks (tapping the ✓ Done button becomes
 * a no-op and the sheet won't close) when `image/*` is combined with file
 * extensions like `.heic,.heif,.dng`. HEIC/HEIF/DNG all report proper
 * `image/*` MIME types on iOS, and desktop browsers still surface them
 * because they're images — so bare `image/*` covers every real case without
 * tripping the iOS picker bug.
 */
export const IMAGE_ACCEPT = 'image/*';
